import React from 'react';
import { Alert, Box, Button, Chip, Paper, Typography } from '@mui/material';
import { MdPeople, MdPersonAdd, MdShoppingBag, MdRepeat, MdCurrencyRupee, MdWorkspacePremium, MdWarningAmber, MdPersonOff, MdShoppingCart } from 'react-icons/md';
import TabbedReport, { pill, TwoLine, Dash, pctChip, shareBar } from './TabbedReport';
import { FONT, inr, fmtDate } from './reportShared';

const SEG = {
  vip: ['VIP', '#fef3c7', '#92400e'],
  repeat: ['Repeat', '#dcfce7', '#166534'],
  new: ['New', '#e0f2fe', '#075985'],
  at_risk: ['At risk', '#ffedd5', '#9a3412'],
  lost: ['Lost', '#fee2e2', '#991b1b'],
  never: ['Never ordered', '#f1f5f9', '#475569'],
};
const segChip = (k) => {
  const [text, bg, fg] = SEG[k] || [k, '#f1f5f9', '#475569'];
  return <Chip size="small" label={text} sx={pill(bg, fg)} />;
};
const money = (key, label, bold) => ({ key, label, numeric: true, bold, text: (r) => r[key], cell: (r) => inr(r[key]), print: (r) => inr(r[key]) });
const num = (key, label, bold) => ({ key, label, numeric: true, bold, text: (r) => r[key] ?? '' });

const tabs = [
  {
    key: 'customers',
    label: 'Customers',
    defaultSort: 'spend_desc',
    sorts: [['spend_desc', 'Lifetime spend: high to low'], ['orders_desc', 'Most orders'], ['recent_order', 'Ordered most recently'], ['joined_desc', 'Newest signups'], ['idle_desc', 'Longest since last order'], ['cart_desc', 'Cart value: high to low']],
    rowAlert: (r) => r.segment === 'at_risk' && r.spend > 0,
    columns: [
      { key: 'name', label: 'Customer', text: (r) => r.name, cell: (r) => <TwoLine top={r.name} bottom={[r.phone, r.email].filter(Boolean).join(' · ')} />, extra: (r) => ({ Phone: r.phone, Email: r.email, 'Customer ID': r.customerId }) },
      { key: 'type', label: 'Type', center: true, text: (r) => r.type, cell: (r) => <Chip size="small" label={r.type} sx={r.type === 'B2B' ? pill('#ede9fe', '#5b21b6') : pill('#e0f2fe', '#075985')} /> },
      { key: 'segment', label: 'Segment', text: (r) => SEG[r.segment]?.[0], cell: (r) => segChip(r.segment) },
      { key: 'location', label: 'Location', text: (r) => [r.city, r.state].filter(Boolean).join(', ') },
      { key: 'joinedAt', label: 'Joined', text: (r) => fmtDate(r.joinedAt), extra: (r) => ({ 'Signed Up Via': r.provider, Device: r.device }) },
      num('orders', 'Orders', true),
      money('spend', 'Lifetime Spend', true),
      money('aov', 'Avg Order'),
      { key: 'lastOrder', label: 'Last Order', text: (r) => (r.lastOrder ? fmtDate(r.lastOrder) : ''), cell: (r) => (r.lastOrder ? <TwoLine top={fmtDate(r.lastOrder)} bottom={`${r.daysSinceOrder} days ago`} /> : <Dash />) },
      { key: 'returned', label: 'Returns', numeric: true, text: (r) => r.returned, cell: (r) => (r.returned ? <Chip size="small" label={r.returned} sx={pill('#ffedd5', '#9a3412')} /> : 0) },
      { key: 'cartValue', label: 'In Cart', numeric: true, text: (r) => r.cartValue, cell: (r) => (r.cartValue ? <span style={{ color: '#b45309', fontWeight: 700 }}>{inr(r.cartValue)}</span> : <Dash />) },
      { key: 'marketable', label: 'Promo Opt-in', center: true, text: (r) => (r.marketable ? 'Yes' : 'No'), cell: (r) => (r.marketable ? <Chip size="small" label="Yes" sx={pill('#dcfce7', '#166534')} /> : <Dash />) },
    ],
  },
  {
    key: 'segments',
    label: 'Segments',
    columns: [
      { key: 'segment', label: 'Segment', text: (r) => r.segment, cell: (r) => segChip(r.key) },
      num('customers', 'Customers', true),
      { key: 'sharePct', label: '% of Customers', text: (r) => `${r.sharePct}%`, cell: (r) => shareBar(r.sharePct) },
      num('orders', 'Orders'),
      money('spend', 'Lifetime Spend', true),
      { key: 'revenueSharePct', label: '% of Revenue', text: (r) => `${r.revenueSharePct}%`, cell: (r) => shareBar(r.revenueSharePct, 'linear-gradient(90deg,#f59e0b,#ea580c)') },
      money('avgLtv', 'Avg LTV'),
      money('aov', 'Avg Order'),
      num('marketable', 'Reachable (opted in)'),
      money('cartValue', 'Sitting in Carts'),
    ],
  },
  {
    key: 'acquisition',
    label: 'Acquisition',
    columns: [
      { key: 'month', label: 'Signup Month', bold: true, text: (r) => r.month },
      num('signups', 'Signups', true),
      num('ordered', 'Placed an Order'),
      { key: 'conversionPct', label: 'Signup → Buyer', center: true, text: (r) => `${r.conversionPct}%`, cell: (r) => pctChip(r.conversionPct, { good: 40, warn: 20 }) },
      num('repeat', 'Came Back'),
      { key: 'repeatPct', label: 'Repeat %', center: true, text: (r) => (r.repeatPct === null ? '' : `${r.repeatPct}%`), cell: (r) => pctChip(r.repeatPct, { good: 40, warn: 20 }) },
      money('spend', 'Spend to Date', true),
      money('ltvPerSignup', 'Value per Signup'),
      { key: 'signupMethods', label: 'Signed Up Via', text: (r) => r.signupMethods },
      { key: 'devices', label: 'Devices', text: (r) => r.devices },
    ],
  },
  {
    key: 'locations',
    label: 'By State',
    columns: [
      { key: 'state', label: 'State', bold: true, text: (r) => r.state },
      num('customers', 'Customers'),
      num('buyers', 'Buyers'),
      num('orders', 'Orders'),
      money('spend', 'Revenue', true),
      { key: 'revenueSharePct', label: '% of Revenue', text: (r) => `${r.revenueSharePct}%`, cell: (r) => shareBar(r.revenueSharePct) },
      money('aov', 'Avg Order'),
      { key: 'repeatPct', label: 'Repeat %', center: true, text: (r) => (r.repeatPct === null ? '' : `${r.repeatPct}%`), cell: (r) => pctChip(r.repeatPct, { good: 40, warn: 20 }) },
    ],
  },
];

const SegmentStrip = ({ stats, goTo, isActive }) => {
  const items = [
    ['vip', stats.vip],
    ['at_risk', stats.atRisk],
    ['lost', stats.lost],
    ['never', stats.never],
  ];
  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
      <Typography sx={{ fontWeight: 800, fontFamily: FONT, mr: 1 }}>Quick segments:</Typography>
      {items.map(([k, n]) => {
        const [text, bg, fg] = SEG[k];
        const active = isActive('customers', { segment: k });
        return (
          <Box key={k} onClick={() => goTo('customers', { segment: active ? '' : k })} sx={{ cursor: 'pointer', px: 1.5, py: 0.75, borderRadius: '10px', background: bg, color: fg, fontWeight: 800, fontFamily: FONT, fontSize: 11.5, outline: active ? `2px solid ${fg}` : 'none' }}>
            {text}: {(n || 0).toLocaleString('en-IN')}
          </Box>
        );
      })}
    </Paper>
  );
};

const CustomerReport = () => (
  <TabbedReport
    route="reports/customers"
    title="Customer Report"
    subtitle="Who your customers are, how they buy, and who's slipping away — dates filter by signup date"
    filePrefix="Customers"
    tabs={tabs}
    searchLabel="Search name / phone / email / city"
    filters={[
      { key: 'segment', label: 'Segment', tabs: ['customers'], options: Object.entries(SEG).map(([value, v]) => ({ value, label: v[0] })) },
      { key: 'type', label: 'Customer type', options: [{ value: 'customer', label: 'B2C (consumer)' }, { value: 'business', label: 'B2B (business)' }] },
      { key: 'state', label: 'State', options: (data) => (data?.filterOptions?.states || []).map((s) => ({ value: s, label: s })) },
    ]}
    cards={(s, { goTo, isActive }) => [
      { label: 'Customers', value: (s.customers || 0).toLocaleString('en-IN'), icon: MdPeople, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `${s.buyers || 0} have ordered · ${s.b2b || 0} B2B`, onClick: () => goTo('customers'), active: isActive('customers') },
      { label: 'New in 30 Days', value: (s.newSignups30 || 0).toLocaleString('en-IN'), icon: MdPersonAdd, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: s.avgDaysToFirstOrder === null || s.avgDaysToFirstOrder === undefined ? 'Signups' : `First order after ${s.avgDaysToFirstOrder} days on avg`, onClick: () => goTo('acquisition'), active: isActive('acquisition') },
      { label: 'Active Buyers', value: (s.active90 || 0).toLocaleString('en-IN'), icon: MdShoppingBag, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: 'Ordered in the last 90 days' },
      { label: 'Repeat Rate', value: s.repeatRate === null || s.repeatRate === undefined ? '—' : `${s.repeatRate}%`, icon: MdRepeat, gradient: 'linear-gradient(135deg,#14b8a6,#0d9488)', sub: 'Buyers with 2+ orders', onClick: () => goTo('segments'), active: isActive('segments') },
      { label: 'Avg Lifetime Value', value: inr(s.avgLtv), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: `Avg order ${inr(s.aov)}` },
      { label: 'VIP Customers', value: (s.vip || 0).toLocaleString('en-IN'), icon: MdWorkspacePremium, gradient: 'linear-gradient(135deg,#eab308,#ca8a04)', sub: '5+ orders or top-10% spend', onClick: () => goTo('customers', { segment: 'vip' }), active: isActive('customers', { segment: 'vip' }) },
      { label: 'At Risk', value: (s.atRisk || 0).toLocaleString('en-IN'), icon: MdWarningAmber, gradient: 'linear-gradient(135deg,#f97316,#c2410c)', sub: `${s.lost || 0} already lost (180d+)`, onClick: () => goTo('customers', { segment: 'at_risk' }), active: isActive('customers', { segment: 'at_risk' }) },
      { label: 'Never Ordered', value: (s.never || 0).toLocaleString('en-IN'), icon: MdPersonOff, gradient: 'linear-gradient(135deg,#64748b,#334155)', sub: 'Signed up, no purchase', onClick: () => goTo('customers', { segment: 'never' }), active: isActive('customers', { segment: 'never' }) },
      { label: 'Sitting in Carts', value: inr(s.cartValue), icon: MdShoppingCart, gradient: 'linear-gradient(135deg,#ec4899,#db2777)', sub: `${s.cartsWithItems || 0} customers with items`, onClick: () => goTo('customers', { sortBy: 'cart_desc' }), active: isActive('customers', { sortBy: 'cart_desc' }) },
    ]}
    panel={(s, ctx) => <SegmentStrip stats={s} {...ctx} />}
    banner={(s, { goTo }) =>
      s.atRisk > 0 ? (
        <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => goTo('customers', { segment: 'at_risk' })}>View</Button>}>
          {s.atRisk} customer(s) haven't ordered in 3–6 months — a good audience for a win-back campaign before they're lost.
        </Alert>
      ) : null
    }
  />
);

export default CustomerReport;
